/* eslint-disable no-else-return */
/* eslint-disable arrow-body-style */
import React, { useMemo, useEffect } from 'react';
import { useOption } from '@/pages/dashboard/utils/customHook';
// import CardBox from '@/pages/dashboard/components/CardBox';
import { SegmentOption } from 'tea-component/lib/segment/SegmentOption';
import classNames from 'classnames';
import './index.scss';
import { TMetricObj } from '../TabComponent/_type';
import { useRenderList } from './useRenderList';
import AigcCardBox from '@/pages/aigc/components/AigcCardBox';
import { IAdditionalTableField, TXAxis, TYAxisItem } from '../AIContent/_type';
import { IDimensionInfo, IMetricInfo, IFilterInfo } from '../AIContent/hooks';
import DownloadSettingsModal from '../DownloadSettingsModal/index';
import { useDownloadFlow } from '../Download/hooks/useDownloadFlow';

interface IProps {
  // 标题
  titleCom?: React.ReactNode;
  subTitleCom?: React.ReactNode;
  middleContentCom?: React.ReactNode;
  downloadTitle?: string;
  className?: string;
  style?: React.CSSProperties;
  // format后的数据
  data: {
    chartData: string[];
    loading: boolean;
    errorMsg: string;
  };
  // 表格表头配置
  options?: { [key: string]: string; }[];
  componentSegment?: SegmentOption[];
  defaultActiveTab: number;
  height: number;
  metricObj: TMetricObj;
  toolTipsFormatter: string;
  yAxisFormatter: string;
  filterVal: { [key: string]: string; };
  tableInfo: { [key: string]: string; };
  chatType: string | string[];
  // 后端传入的x轴配置，当前值用到数组第一个值
  xAxis: TXAxis;
  // 是否是copilot模块
  isCopilot?: boolean;
  // 浮窗时间格式化
  toolTipsDateFormatter: string;
  // 图表数据id
  chartDataId: string;
  // 维度信息（用于下载弹窗）
  dimensionInfo?: IDimensionInfo[];
  // 原始数据（用于生成下载数据）
  rawData?: any[];
  // 指标信息（用于生成下载数据）
  metricsInfo?: IMetricInfo[];
  // 图例信息（用于生成下载数据）
  legends?: any[];
  // 筛选器信息（用于级联筛选器）
  filterInfo?: IFilterInfo[];
  // yAxis 配置（包含所有 ECharts yAxis 配置项）
  yAxisConfig?: TYAxisItem | null;
  // 额外的表格字段
  additional_table_field?: IAdditionalTableField[];
  // 右侧操作区插槽（如全屏按钮等）
  operationSlot?: React.ReactNode;
  // 全屏相关
  requestFullScreen?: () => void;
  exitFullScreen?: () => void;
  isFullScreen?: boolean;
  // 图表类型状态管理（受控模式，防止全屏切换时丢失状态）
  activeChartType?: string;
  onActiveChartTypeChange?: (type: string, svgType: string) => void;
}

export default function AIChartContent(props: IProps) {
  const {
    titleCom,
    subTitleCom,
    downloadTitle,
    className,
    style = {},
    middleContentCom,
    yAxisFormatter,
    toolTipsFormatter,
    data,
    height = 290,
    filterVal,
    tableInfo,
    chatType,
    metricObj,
    xAxis,
    isCopilot = false,
    toolTipsDateFormatter,
    chartDataId,
    dimensionInfo = [],
    rawData = [],
    metricsInfo = [],
    legends = [],
    filterInfo = [],
    yAxisConfig,
    additional_table_field,
    operationSlot,
    requestFullScreen,
    exitFullScreen,
    isFullScreen,
    activeChartType,
    onActiveChartTypeChange
  } = props || {};

  const { chartData, loading = false, errorMsg: error = '' } = data || {};
  const componentData = { chart: chartData || [] };

  const { xAxisData, legendData, lineSeries, barSeries, isNodata } = useOption(componentData);
  const { renderList, defaultActiveTab, selected, setSelected, selectOption } = useRenderList({
    filterVal,
    metricObj,
    yAxisFormatter,
    chatType,
    xAxis,
    componentData,
    xAxisData,
    legendData,
    tableInfo,
    toolTipsFormatter,
    lineSeries,
    barSeries,
    height,
    isCopilot,
    toolTipsDateFormatter,
    chartDataId,
    yAxisConfig: yAxisConfig || null,
    isFullScreen
  });
  
  // 确保 selected 和 activeChartType 保持同步
  useEffect(() => {
    if (activeChartType && activeChartType !== selected) {
      setSelected(activeChartType);
    }
  }, [activeChartType, selected, setSelected]);
  // echart 图插件功能展示


  // 下载流程（配置、数据、执行、弹窗控制）
  const {
    downloadData,
    modalVisible,
    hasFiltersOrMetrics,
    openDownload,
    closeDownload,
    confirmDownload,
  } = useDownloadFlow({
    xAxis,
    legends,
    filterInfo,
    dimensionInfo,
    metricsInfo,
    filterVal,
    currentMetric: metricObj?.data_key || '',
    rawData,
    metricObj,
    downloadTitle,
    tableInfo,
    additional_table_field,
  });

  const copilotRender = useMemo(() => {
    if (!isCopilot) return null;
    
    return (renderList || []).find?.(item => item?.type === selected)?.component || null;
  }, [renderList, selected, isCopilot]);

  return (
    <>
      <AigcCardBox
        className={classNames(className, 'dba-new-chart-card')}
        style={style}
        height={height}
        titleCom={titleCom ?? null}
        subTitleCom={subTitleCom ?? null}
        middleContentCom={middleContentCom ?? null}
        bordered={false}
        renderList={renderList || []}
        // 确保每次指标更新都重新渲染，比如：当前指标有折线图，但是切换后的指标无折线图，那么切换后，对应图标模块展示为空
        // key={metricObj?.data_key}
        defaultAvtive={defaultActiveTab}
        downloadClick={openDownload}
        selectOption={selectOption || undefined}
        isLoading={loading}
        isError={!!error}
        isNodata={isNodata}
        dataKey={'metric'}
        setSelected={(val)=>{
          // 如果是下载，则不切换图标，直接进行下载
          if(val === 'download') {
            openDownload();
            return;
          }
          if (val === 'fullScreen') {
            if (isFullScreen) {
              exitFullScreen();
              return;
            }
            requestFullScreen();
            return;
          }
          setSelected(val);
          // 同步到父组件的chartType状态
          if (onActiveChartTypeChange) {
            const item = renderList.find(r => r.type === val);
            if (item) {
              onActiveChartTypeChange(val, item.svgType);
            }
          }
        }}
        selected={activeChartType || selected}
        operationSlot={operationSlot}
        isFullScreen={isFullScreen}
        activeChart={activeChartType}
        onActiveChartChange={onActiveChartTypeChange}
        // eslint-disable-next-line react/no-children-prop
        children={isCopilot ? copilotRender : undefined}
      />
      {/* 下载设置弹窗 */}
      {hasFiltersOrMetrics && (
        <DownloadSettingsModal
          visible={modalVisible}
          onCancel={closeDownload}
          onConfirm={confirmDownload}
          currentFilterVal={filterVal}
          currentMetric={metricObj?.data_key || ''}
          showFilters={downloadData.filters}
          showMetrics={downloadData.metrics}
          effectiveSelected={downloadData.effectiveSelected}
          allFilters={downloadData.allFilters}
          getFilteredOptions={downloadData.getFilteredOptions}
          filterMetricsByCascade={downloadData.filterMetricsByCascade}
          getDimensionName={downloadData.getDimensionName}
          metricsInfo={metricsInfo}
        />
      )}
    </>
  );
}
